<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import mockup1 from '../assets/mockups/mockup.png';
import mockup2 from '../assets/mockups/mockup2.png';
import mockup3 from '../assets/mockups/mockup3.png';
import mockup4 from '../assets/mockups/mockup6.png';

gsap.registerPlugin(ScrollTrigger);

const activeImageIndex = ref(0);

const images = [
    mockup1,
    mockup2, // User requested mockup2 for the second paragraph
    mockup3,
    mockup4
];

// Definition of the 4 icons corresponding to each scroll position
const icons = [
    { id: 0, class: 'blue', icon: 'deployed_code' },
    { id: 1, class: 'green', icon: 'cloud' },
    { id: 2, class: 'orange', icon: 'group' },
    { id: 3, class: 'red', icon: 'lock' }
];

// Bloques de texto (uno por ícono/imagen)
const textBlocks = [
    {
        title: 'Automatiza tu <span class="text-blue">mantenimiento</span> sin complicaciones',
        desc: 'Olvídate del papeleo, las hojas de cálculo perdidas y el seguimiento manual. Nuestra solución móvil centraliza absolutamente todo tu plan de acción preventivo y correctivo, colocándolo directamente en el bolsillo de cada técnico.',
    },
    {
        title: 'Flujos de trabajo <span class="text-green">ágiles</span>',
        desc: 'Transforma el estado de un ticket de "pendiente" a "completado" en cuestión de segundos. Reporta anomalías en tiempo real, añade contexto visual y catapulta la productividad de tu equipo en terreno a niveles nunca vistos.',
    },
    {
        title: 'Captura de <span class="text-orange">evidencia</span> al vuelo',
        desc: 'Asegura un respaldo blindado en cada visita. Tus técnicos pueden adjuntar fotografías en alta resolución, grabar notas de voz descriptivas y recabar firmas digitales in-situ, garantizando una trazabilidad impecable en cada intervención.',
    },
    {
        title: 'Reportes de impacto <span class="text-red">inmediato</span>',
        desc: 'Toma decisiones basadas en datos concretos, no en suposiciones. Genera informes gerenciales de cumplimiento, costos y tiempos de respuesta con un solo clic. Exporta información pulida y lista para brillar en cualquier auditoría.',
    },
];

// Posición visual de un ícono respecto al bloque activo (front-most = 0)
const visualIndex = (iconId: number, activeId: number) => {
    const total = icons.length;
    return (iconId - activeId + total) % total;
};

// Desktop: el cluster único usa el índice del bloque activo global
const getIconVisualIndex = (iconId: number) => visualIndex(iconId, activeImageIndex.value);

// Desktop: desplaza el cluster único a la altura del bloque activo
const positionIcons = (index: number, animate = true) => {
    const blocks = gsap.utils.toArray<HTMLElement>('.text-block');
    const block = blocks[index];
    if (!block) return;
    gsap.to('.desktop-icons', {
        y: block.offsetTop - 96,
        duration: animate ? 0.6 : 0,
        ease: 'power2.out',
        overwrite: true,
    });
};

// Scroll to specific block when clicking an icon
const scrollToBlock = (index: number) => {
    const blocks = document.querySelectorAll('.text-block');
    if (blocks && blocks[index]) {
        const rect = blocks[index].getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        // Desplazamos la ventana un poco más arriba (aprox 40% del alto) para que active instantáneamente el 'start'
        const targetY = rect.top + scrollTop - (window.innerHeight * 0.4);

        window.scrollTo({
            top: targetY,
            behavior: 'smooth'
        });
    }
};

onMounted(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
        // --- New Sticky Section Animation ---
        ScrollTrigger.create({
            trigger: '.sticky-mobile-feature',
            start: 'top top',
            end: 'bottom bottom',
            pin: '.phone-sticky-container', // Pinning interno para máxima estabilidad X
            pinSpacing: false,
            invalidateOnRefresh: true,
        });

        // Animate text blocks
        const blocks = gsap.utils.toArray<HTMLElement>('.text-block');
        blocks.forEach((block, index) => {
            gsap.from(block, {
                opacity: 0,
                y: 60,
                duration: 1,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: block,
                    start: 'top 85%',
                    end: 'top 50%',
                    scrub: true,
                    invalidateOnRefresh: true,
                    onEnter: () => {
                        activeImageIndex.value = Math.min(index, images.length - 1);
                        positionIcons(index);
                    },
                    onLeaveBack: () => {
                        // Al hacer scroll arriba (retroceder), activamos el bloque anterior inmediatamente
                        if (index > 0) {
                            activeImageIndex.value = index - 1;
                            positionIcons(index - 1);
                        }
                    },
                },
            });
        });

        // Entrada del mockup: fromTo + once para que no quede oculto si el refresh llega tarde
        gsap.fromTo('.phone-mockup-sticky',
            { scale: 0.8, opacity: 0 },
            {
                scale: 1,
                opacity: 1,
                duration: 1.5,
                ease: 'expo.out',
                scrollTrigger: {
                    trigger: '.sticky-mobile-feature',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                    once: true,
                },
            }
        );

        // Recoloca el cluster desktop al recalcular y posición inicial
        ScrollTrigger.addEventListener('refresh', () => positionIcons(activeImageIndex.value, false));
        positionIcons(0, false);
    });

    // Las imágenes cambian la altura del layout: refrescamos cuando todas cargan
    let pending = images.length;
    images.forEach((src) => {
        const img = new Image();
        const done = () => {
            if (--pending <= 0) ScrollTrigger.refresh();
        };
        img.onload = done;
        img.onerror = done;
        img.src = src;
    });
    // Refresh extra tras el primer frame por si Lenis se inicializa diferido
    requestAnimationFrame(() => ScrollTrigger.refresh());

    onUnmounted(() => {
        mm.revert();
    });
});
</script>

<template>
    <section class="sticky-mobile-feature">
        <div class="content-wrapper">
            <div class="sticky-layout-grid">
                <div class="text-column">

                    <!-- Desktop: un único cluster que se desplaza junto al bloque activo -->
                    <div class="icons-overlap desktop-icons">
                        <div v-for="item in icons" :key="item.id" class="overlap-icon" :class="item.class" :style="{
                            zIndex: icons.length - getIconVisualIndex(item.id),
                            '--offset-x': `${getIconVisualIndex(item.id) * 38}px`,
                            filter: getIconVisualIndex(item.id) === 0 ? 'grayscale(0)' : 'grayscale(1)'
                        }" @click="scrollToBlock(item.id)">
                            <span class="material-symbols-outlined">{{ item.icon }}</span>
                        </div>
                    </div>

                    <div v-for="(block, bi) in textBlocks" :key="bi" class="text-block"
                        :class="{ first: bi === 0 }">
                        <!-- Mobile: un cluster por cada bloque -->
                        <div class="icons-overlap mobile-icons">
                            <div v-for="item in icons" :key="item.id" class="overlap-icon" :class="item.class" :style="{
                                zIndex: icons.length - visualIndex(item.id, bi),
                                '--offset-x': `${visualIndex(item.id, bi) * 38}px`,
                                filter: visualIndex(item.id, bi) === 0 ? 'grayscale(0)' : 'grayscale(1)'
                            }" @click="scrollToBlock(item.id)">
                                <span class="material-symbols-outlined">{{ item.icon }}</span>
                            </div>
                        </div>
                        <h2 class="section-title-large" v-html="block.title"></h2>
                        <p class="section-desc">{{ block.desc }}</p>
                    </div>
                </div>

                <div class="visual-column">
                    <div class="phone-sticky-container">
                        <transition name="fade">
                            <img :key="activeImageIndex" :src="images[activeImageIndex]" alt="App Preview"
                                class="phone-mockup-sticky" />
                        </transition>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* New Sticky Section Styles */
.sticky-mobile-feature {
    padding: 100px 0;
    background-color: #fff;
    position: relative;
}

/* Mitad derecha de la pantalla con fondo azul de marca */
.sticky-mobile-feature::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    background-color: #fff;
    z-index: 0;
}

.content-wrapper {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 40px;
    position: relative;
    z-index: 1;
}

.sticky-layout-grid {
    display: flex;
    gap: 80px;
    position: relative;
}

.text-column {
    flex: 0 0 55%;
    /* Ancho fijo para evitar saltos en X */
    display: flex;
    flex-direction: column;
    gap: 50vh;
    /* Mayor distancia para forzar la separación de pantallas */
    padding-bottom: 200px;
    position: relative;
    /* important for absolute children */
}

.visual-column {
    flex: 0 0 45%;
    /* Ancho fijo para que el eje X sea inamovible */
    display: flex;
    justify-content: flex-end;
    /* Alinea a la derecha en el eje X */
    align-items: flex-start;
    min-height: 100%;
}

.phone-sticky-container {
    position: sticky;
    top: 0;
    height: 90vh;
    z-index: 10;
    width: 100%;
    display: flex;
    justify-content: center;
    /* Alinea a la derecha en el eje X */
    align-items: center;
    /* Centrado vertical en el eje Y */
}

.text-block.first {
    margin-top: 42px;
}

.text-block {
    max-width: 580px;
    min-height: 30vh;
    /* Agregamos altura mínima para que ocupe espacio similar a un bloque independiente */
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.overlap-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    border: 3px solid #fff;
    cursor: pointer;
    /* Añadimos cursor pointer para indicar que es clickeable */

    /* Animation definition for smooth rearranging */
    position: absolute;
    left: 0;
    transform: translateX(var(--offset-x, 0px));
    transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1),
        z-index 0s step-end,
        filter 0.4s ease;
}

.overlap-icon:hover {
    z-index: 21 !important;

    /* !important es necesario para vencer al zIndex inline de Vue */
    transform: translateX(var(--offset-x, 0px)) translateY(-10px);
    filter: grayscale(0) !important;
}

.icons-overlap {
    display: flex;
    height: 56px;
    width: fit-content;
    z-index: 20;
}
/* Desktop: cluster único que se desplaza (GSAP mueve su Y) */
.desktop-icons {
    position: absolute;
    top: 30px;
    left: 0;
}
/* Mobile: un cluster por bloque, en flujo. Oculto en desktop. */
.mobile-icons {
    position: relative;
    margin-bottom: 24px;
    display: none;
}

.overlap-icon .material-symbols-outlined {
    font-size: 28px;
    user-select: none;
}


.overlap-icon:first-child {
    margin-left: 0;
}

.overlap-icon.blue {
    background-color: #129BEF;
}

.overlap-icon.green {
    background-color: #10B981;
}

.overlap-icon.orange {
    background-color: #F59E0B;
}

.overlap-icon.red {
    background-color: #EF4444;
}

/* Utility text colors matching icons.
   :deep() porque los <span> viven dentro de un v-html y no reciben el data-scope. */
:deep(.text-blue) {
    color: #129BEF;
    font-weight: 800;
}

:deep(.text-green) {
    color: #10B981;
    font-weight: 800;
}

:deep(.text-orange) {
    color: #F59E0B;
    font-weight: 800;
}

:deep(.text-red) {
    color: #EF4444;
    font-weight: 800;
}

.section-title-large {
    font-size: 64px;
    font-weight: 800;
    line-height: 1.1;
    color: #111827;
    letter-spacing: -0.04em;
    margin-bottom: 24px;
}

.feature-title-small {
    font-size: 36px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 20px;
    letter-spacing: -0.02em;
}

.section-desc {
    font-size: 20px;
    line-height: 1.6;
    color: #4B5563;
    font-weight: 500;
}

.phone-mockup-sticky {
    width: 100%;
    max-width: 350px;
    height: auto;
}

.text-accent {
    color: #129BEF;
    font-weight: 700;
}

@media (max-width: 1024px) {
    .sticky-mobile-feature::before {
        display: none;
    }

    .sticky-layout-grid {
        flex-direction: column-reverse;
        gap: 60px;
    }

    .text-column {
        gap: 120px;
        padding-bottom: 60px;
    }

    .phone-sticky-container {
        position: relative;
        top: 0;
    }

    .section-title-large {
        font-size: 42px;
    }

    .visual-column {
        min-height: auto;
    }
}

@media (max-width: 768px) {
    .content-wrapper {
        padding: 0 24px;
    }

    /* En mobile no mostramos el teléfono sticky */
    .visual-column {
        display: none;
    }

    /* Mobile: cluster por bloque en vez del único que se desplaza */
    .desktop-icons {
        display: none;
    }
    .mobile-icons {
        display: flex;
    }

    .section-title-large {
        font-size: 32px;
    }

    .feature-title-small {
        font-size: 26px;
    }

    .section-desc {
        font-size: 18px;
    }
}

/* Image Transition - Original Fade */
/*
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
*/

/* Image Transition - Smooth Crossfade */
.fade-enter-active,
.fade-leave-active {
    transition: all 0.5s ease-in-out;
}

.fade-leave-active {
    position: absolute;
    /* Saca a la imagen saliente del flujo para no desplazar a la entrante */
}

.fade-enter-from {
    opacity: 0;
    /*  transform: translateY(10px); */
}

.fade-leave-to {
    opacity: 0;
    /*  transform: translateY(-10px); */
}
</style>
